import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Modal,
  Stack,
  TextField,
  Divider,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import CloseIcon from '@mui/icons-material/Close';
import {CircleMinus} from 'lucide-react';
import {DateField} from '@mui/x-date-pickers';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {useTheme} from '@mui/material/styles';
import {useSelector} from 'react-redux';
import {useAuthFrontendApis} from '@propelauth/frontend-apis-react';
import {BLACK_100, WHITE_100} from '../../theme';
import Loading from '../../components/Loading';
import {
  useCreateChildOrgMutation,
  useUpdateChildOrgMutation,
} from '../../data/api';
import PlusAddIcon from '../../assets/plus-add.svg';
import {useFlags} from 'launchdarkly-react-client-sdk';

const modalStyles = {
  base: {
    borderRadius: '0.5rem',
    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
    backgroundColor: WHITE_100,
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: '700',
    lineHeight: '120%',
    color: '#043B34',
  },
  divider: {
    borderBottomWidth: '0.0625rem',
    opacity: 1,
    color: '#DDe0DE',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#043B34',
    marginBottom: '8px',
  },
  confirmButton: {
    background: '#26A993',
    borderRadius: '0.5rem',
    paddingVertical: '0.625rem',
    height: '2.5rem',
    '&:hover': {
      backgroundColor: '#26A993',
    },
  },
  confirmButtonLabel: {
    fontSize: '0.875rem',
    lineHeight: '140%',
    fontWeight: '500',
  },
  cancelButton: {
    borderRadius: '0.5rem',
    paddingVertical: '0.6825rem',
    paddingHorizontal: '1rem',
    height: '2.5rem',
    '&:hover': {
      backgroundColor: WHITE_100,
    },
  },
  cancelButtonLabel: {
    color: '#043B34',
    fontSize: '0.875rem',
    fontWeight: '500',
    lineHeight: '130%',
  },
  inputField: {
    '& .MuiOutlinedInput-root': {
      display: 'flex',
      padding: '12px 16px',
      justifyContent: 'space-between',
      alignItems: 'center',
      alignSelf: 'stretch',
      borderRadius: '8px',
      border: '1px solid #E1E5E4',
      height: '42px',
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: 'none',
      },
      '&.Mui-focused': {
        borderColor: '#26A993',
      },
    },
    '& .MuiInputBase-input': {
      padding: '0',
    },
  },
  datePicker: {
    '& .MuiOutlinedInput-root': {
      display: 'flex',
      padding: '12px 16px',
      justifyContent: 'space-between',
      alignItems: 'center',
      alignSelf: 'stretch',
      borderRadius: '8px',
      border: '1px solid #E1E5E4',
      height: '42px',
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: 'none',
      },
      '&.Mui-focused': {
        borderColor: '#26A993',
      },
    },
    '& .MuiInputBase-input': {
      padding: '0',
    },
  },
  adminInputField: {
    '& .MuiOutlinedInput-root': {
      display: 'flex',
      padding: '12px 16px',
      justifyContent: 'space-between',
      alignItems: 'center',
      alignSelf: 'stretch',
      borderRadius: '8px',
      border: '1px solid #E1E5E4',
      height: '42px',
      backgroundColor: '#fff',
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: 'none',
      },
      '&.Mui-focused': {
        borderColor: '#26A993',
        backgroundColor: '#fff',
      },
      '&:hover': {
        borderColor: '#26A993',
      },
    },
    '& .MuiInputBase-input': {
      padding: '0',
      fontSize: '14px',
      fontWeight: 500,
      color: '#162523',
      '&::placeholder': {
        color: '#9AA7A6',
        opacity: 1,
      },
      '&::-webkit-search-cancel-button': {
        display: 'none',
      },
      '&::-webkit-search-decoration': {
        display: 'none',
      },
    },
    '& .MuiAutocomplete-popupIndicator': {
      display: 'none',
    },
    '& .MuiAutocomplete-clearIndicator': {
      marginRight: '8px',
    },
  },
};

const AddCompanyModal = ({open, onClose, onShowSnackbar, editingClient}) => {
  const {
    headOfficeOnParentOrganizationPage:
      headOfficeOnParentOrganizationPageEnabled,
  } = useFlags();
  const [createOrg] = useCreateChildOrgMutation();
  const [updateChildOrg] = useUpdateChildOrgMutation();
  const {fetchOrgMembers} = useAuthFrontendApis();
  const {parentOrg} = useSelector((state) => state.session);
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientLegalName, setClientLegalName] = useState('');
  const [yearEndDate, setYearEndDate] = useState(null);
  const [clientIdentifier, setClientIdentifier] = useState('');
  const [headOfficeId, setHeadOfficeId] = useState('');
  // Admin user search state
  const [showAdminSection, setShowAdminSection] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [nextAdminId, setNextAdminId] = useState(1);
  const searchTimersRef = useRef(new Map());

  // Edit mode state - track original values
  const [originalValues, setOriginalValues] = useState(null);
  const isEditMode = !!editingClient;

  // Check if form has changes in edit mode
  const hasChanges = useMemo(() => {
    if (!isEditMode || !originalValues) return false;

    const validAdminUsers = adminUsers.filter((user) => user.selectedUser);
    const currentAdminIds = validAdminUsers
      .map((user) => user.selectedUser.id)
      .sort();
    const originalAdminIds = [...(originalValues.adminUserIds || [])].sort();

    return (
      clientName.trim() !== originalValues.name ||
      (clientLegalName.trim() || '') !==
        (originalValues.companyLegalName || '') ||
      (yearEndDate && yearEndDate.isValid()
        ? yearEndDate.format('MM-DD')
        : '') !== (originalValues.fiscalYearEnd || '') ||
      (clientIdentifier.trim() || '') !==
        (originalValues.clientIdentifier || '') ||
      (headOfficeId.trim() || '') !== (originalValues.headOfficeId || '') ||
      JSON.stringify(currentAdminIds) !== JSON.stringify(originalAdminIds)
    );
  }, [
    isEditMode,
    originalValues,
    clientName,
    clientLegalName,
    yearEndDate,
    adminUsers,
    clientIdentifier,
    headOfficeId,
  ]);

  // Search parent org users via API
  const searchParentOrgUsers = useCallback(
    async (searchTerm, userIndex) => {
      if (!parentOrg || !searchTerm.trim()) {
        setAdminUsers((prev) =>
          prev.map((user, index) =>
            index === userIndex
              ? {...user, searchResults: [], searchLoading: false}
              : user
          )
        );
        return;
      }

      setAdminUsers((prev) =>
        prev.map((user, index) =>
          index === userIndex ? {...user, searchLoading: true} : user
        )
      );

      try {
        const parentResponse = await fetchOrgMembers(parentOrg.orgId, {
          page_number: 0,
          page_size: 10,
          email_search: searchTerm.trim(),
        });

        let parentUsers = [];

        await parentResponse.handle({
          success: (data) => {
            parentUsers =
              data.users?.map((user) => ({
                id: user.user_id,
                email: user.email,
                name: user.email.split('@')[0],
              })) || [];
          },
          orgNotFound: () => {
            parentUsers = [];
          },
          orgsNotEnabled: () => {
            parentUsers = [];
          },
          unauthorized: () => {
            parentUsers = [];
          },
          unexpectedOrUnhandled: () => {
            parentUsers = [];
          },
        });

        // Filter out users who are already selected in other admin fields
        setAdminUsers((prev) => {
          const selectedUserIds = prev
            .map((user, index) => {
              if (index !== userIndex && user.selectedUser) {
                return user.selectedUser.id;
              }
              return null;
            })
            .filter((id) => id !== null);

          const filteredUsers = parentUsers.filter(
            (user) => !selectedUserIds.includes(user.id)
          );

          return prev.map((user, index) =>
            index === userIndex
              ? {...user, searchResults: filteredUsers, searchLoading: false}
              : user
          );
        });
      } catch (error) {
        setAdminUsers((prev) =>
          prev.map((user, index) =>
            index === userIndex
              ? {...user, searchResults: [], searchLoading: false}
              : user
          )
        );
      }
    },
    [parentOrg, fetchOrgMembers]
  );

  // Debounced search function
  const debouncedSearch = useCallback(
    (searchInput, userIndex) => {
      if (searchTimersRef.current.has(userIndex)) {
        clearTimeout(searchTimersRef.current.get(userIndex));
      }

      const timer = setTimeout(() => {
        if (searchInput.trim()) {
          searchParentOrgUsers(searchInput, userIndex);
        } else {
          setAdminUsers((prev) =>
            prev.map((u, i) =>
              i === userIndex
                ? {...u, searchResults: [], searchLoading: false}
                : u
            )
          );
        }
      }, 300);

      searchTimersRef.current.set(userIndex, timer);
    },
    [searchParentOrgUsers]
  );

  useEffect(() => {
    return () => {
      searchTimersRef.current.forEach((timer) => clearTimeout(timer));
      searchTimersRef.current.clear();
    };
  }, []);

  // Admin user management functions
  const addAdminUser = () => {
    setAdminUsers((prev) => [
      ...prev,
      {
        id: nextAdminId,
        selectedUser: null,
        searchInput: '',
        searchResults: [],
        searchLoading: false,
      },
    ]);
    setNextAdminId((prev) => prev + 1);
  };

  const updateAdminUser = (index, updates) => {
    setAdminUsers((prev) =>
      prev.map((user, i) => (i === index ? {...user, ...updates} : user))
    );
  };

  const removeAdminUser = (index) => {
    if (adminUsers.length > 1) {
      setAdminUsers((prev) => prev.filter((_, i) => i !== index));
    } else {
      setAdminUsers([]);
      setShowAdminSection(false);
    }
  };

  const handleAddAdmin = () => {
    setShowAdminSection(true);
    setAdminUsers([
      {
        id: 0,
        selectedUser: null,
        searchInput: '',
        searchResults: [],
        searchLoading: false,
      },
    ]);
    setNextAdminId(1);
  };

  // Populate form when editing
  useEffect(() => {
    if (open && editingClient) {
      const fiscalYearEnd = editingClient.fiscalYearEnd;
      let yearEndDateValue = null;
      if (fiscalYearEnd) {
        try {
          const [month, day] = fiscalYearEnd.split('-');
          const currentYear = new Date().getFullYear();
          yearEndDateValue = new Date(
            currentYear,
            parseInt(month) - 1,
            parseInt(day)
          );
        } catch (error) {
          console.error('Error parsing fiscal year end:', error);
        }
      }

      setClientName(editingClient.name || '');
      setClientLegalName(editingClient.companyLegalName || '');
      setClientIdentifier(editingClient.externalId || '');
      setHeadOfficeId(editingClient.headOfficeId || '');
      setYearEndDate(yearEndDateValue ? dayjs(yearEndDateValue) : null);

      // Set up admin users if they exist
      if (editingClient.admins && editingClient.admins.length > 0) {
        setShowAdminSection(true);
        const adminUsersData = editingClient.admins.map((admin, index) => ({
          id: index,
          selectedUser: {
            id: admin.userId || admin.id,
            email: admin.email,
            firstName: admin.firstName,
            lastName: admin.lastName,
          },
          searchInput: admin.email,
          searchResults: [],
          searchLoading: false,
        }));
        setAdminUsers(adminUsersData);
        setNextAdminId(adminUsersData.length);
      } else {
        setShowAdminSection(false);
        setAdminUsers([]);
        setNextAdminId(1);
      }

      // Store original values for comparison
      setOriginalValues({
        name: editingClient.name || '',
        companyLegalName: editingClient.companyLegalName || '',
        fiscalYearEnd: editingClient.fiscalYearEnd || '',
        adminUserIds:
          editingClient.admins?.map((admin) => admin.userId || admin.id) || [],
        clientIdentifier: editingClient.externalId || '',
        headOfficeId: editingClient.headOfficeId || '',
      });
    }
  }, [open, editingClient]);

  // Clear form when modal closes
  useEffect(() => {
    if (!open) {
      setClientName('');
      setClientLegalName('');
      setClientIdentifier('');
      setHeadOfficeId('');
      setYearEndDate(null);
      setShowAdminSection(false);
      setAdminUsers([]);
      setNextAdminId(1);
      setOriginalValues(null);
    }
  }, [open]);

  const handleCreate = async () => {
    if (!isButtonEnabled) return;

    setIsLoading(true);

    try {
      const validAdminUsers = adminUsers.filter((user) => user.selectedUser);
      const adminUserIds = validAdminUsers.map((user) => user.selectedUser.id);

      if (isEditMode) {
        // Update mode - only send changed fields
        const currentValues = {
          name: clientName.trim(),
          companyLegalName: clientLegalName.trim() || '',
          fiscalYearEnd:
            yearEndDate && yearEndDate.isValid()
              ? yearEndDate.format('MM-DD')
              : '',
          adminUserIds,
          externalId: clientIdentifier.trim() || '',
          headOfficeId: headOfficeId.trim() || '',
        };

        const changedFields = {};

        if (currentValues.name !== originalValues.name) {
          changedFields.name = currentValues.name;
        }
        if (
          currentValues.companyLegalName !==
          (originalValues.companyLegalName || '')
        ) {
          changedFields.companyLegalName = currentValues.companyLegalName;
        }
        if (
          currentValues.fiscalYearEnd !== (originalValues.fiscalYearEnd || '')
        ) {
          changedFields.fiscalYearEnd = currentValues.fiscalYearEnd;
        }
        if (
          currentValues.externalId !== (originalValues.clientIdentifier || '')
        ) {
          changedFields.externalId = currentValues.externalId;
        }
        if (
          currentValues.headOfficeId !== (originalValues.headOfficeId || '')
        ) {
          changedFields.headOfficeId = currentValues.headOfficeId;
        }

        const originalAdminIds = [
          ...(originalValues.adminUserIds || []),
        ].sort();
        const currentAdminIds = [...adminUserIds].sort();
        if (
          JSON.stringify(originalAdminIds) !== JSON.stringify(currentAdminIds)
        ) {
          changedFields.adminUserIds = adminUserIds;
        }

        if (Object.keys(changedFields).length === 0) {
          onShowSnackbar({
            message: 'No changes to save',
            severity: 'info',
          });
          onClose();
          return;
        }

        await updateChildOrg({
          orgId: editingClient.org,
          ...changedFields,
        }).unwrap();

        onShowSnackbar({
          message: `Client "${clientName}" updated successfully`,
          severity: 'success',
        });
      } else {
        // Create mode
        const orgData = {
          parentOrganizationId: parentOrg.orgId,
          name: clientName.trim(),
          externalId: clientIdentifier.trim(),
          ...(clientLegalName.trim() && {
            companyLegalName: clientLegalName.trim(),
          }),
          ...(yearEndDate &&
            yearEndDate.isValid() && {
              fiscalYearEnd: yearEndDate.format('MM-DD'),
            }),
          ...(adminUserIds.length > 0 && {adminUserIds}),
          ...(headOfficeId.trim() && {headOfficeId: headOfficeId.trim()}),
        };

        await createOrg(orgData).unwrap();

        onShowSnackbar({
          message: `Client "${clientName}" created successfully`,
          severity: 'success',
        });
      }

      // Clear form and close modal
      setClientName('');
      setClientLegalName('');
      setClientIdentifier('');
      setYearEndDate(null);
      setShowAdminSection(false);
      setAdminUsers([]);
      setNextAdminId(1);
      setOriginalValues(null);
      onClose();
    } catch (error) {
      console.error(
        `Failed to ${isEditMode ? 'update' : 'create'} organization:`,
        error
      );

      let errorMessage =
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        `Failed to ${isEditMode ? 'update' : 'create'} client`;

      // Parse error.response.data.message as JSON and check for name validation error
      if (error?.response?.data?.message) {
        try {
          const parsedError = JSON.parse(error.response.data.message);
          if (
            parsedError?.name &&
            Array.isArray(parsedError.name) &&
            parsedError.name.some((msg) =>
              msg.includes('That name is already in use')
            )
          ) {
            errorMessage = 'Client name already taken';
          }
        } catch (parseError) {
          // If parsing fails, use the default error message
        }
      }

      onShowSnackbar({
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setClientName('');
      setClientLegalName('');
      setClientIdentifier('');
      setHeadOfficeId('');
      setYearEndDate(null);
      setShowAdminSection(false);
      setAdminUsers([]);
      setNextAdminId(1);
      setOriginalValues(null);
      onClose();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && isButtonEnabled && !isLoading) {
      handleCreate();
    }
  };

  const handleClientNameChange = (event) => {
    const value = event.target.value;
    // Only allow alphanumeric characters, spaces, and underscores
    const sanitizedValue = value.replace(/[^a-zA-Z0-9\s_]/g, '');
    setClientName(sanitizedValue);
  };

  const isFormValid =
    clientName.trim() && clientIdentifier.trim() && (parentOrg || isEditMode);

  // In edit mode, button should be enabled only if form is valid AND there are changes
  const isButtonEnabled = isEditMode ? isFormValid && hasChanges : isFormValid;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        position="absolute"
        sx={{left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
      >
        <Box width="28rem" minWidth="28rem" maxWidth="28rem">
          <Stack direction="column" sx={modalStyles.base}>
            <Stack
              direction="row"
              paddingY="1rem"
              paddingLeft="1.5rem"
              paddingRight="1rem"
              flex={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography sx={modalStyles.title}>
                {isEditMode ? 'Edit client' : 'Add client'}
              </Typography>
              <IconButton onClick={handleClose} disabled={isLoading}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Divider orientation="horizontal" sx={modalStyles.divider} />
            <Stack
              direction="column"
              paddingTop="1.25rem"
              paddingBottom="1.5rem"
              paddingX="1.5rem"
              spacing="1rem"
              sx={{
                maxHeight: '40vh',
                overflowY: 'auto',
              }}
            >
              {/* Client Name Input */}
              <Stack direction="column" spacing={1} paddingBottom="10px">
                <Typography sx={modalStyles.label}>
                  Client Name <span style={{color: '#26A993'}}>*</span>
                </Typography>
                <TextField
                  variant="outlined"
                  value={clientName}
                  onChange={handleClientNameChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Stripe"
                  disabled={isLoading}
                  autoFocus
                  sx={modalStyles.inputField}
                />
              </Stack>

              {/* Client Legal Name Input */}
              <Stack direction="column" spacing={1} paddingBottom="10px">
                <Typography sx={modalStyles.label}>
                  Client Legal Name
                </Typography>
                <TextField
                  variant="outlined"
                  value={clientLegalName}
                  onChange={(event) => setClientLegalName(event.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Stripe LLC"
                  disabled={isLoading}
                  sx={modalStyles.inputField}
                />
              </Stack>

              <Stack direction="column" spacing={1} paddingBottom="10px">
                <Typography sx={modalStyles.label}>
                  Client Identifier <span style={{color: '#26A993'}}>*</span>
                </Typography>
                <TextField
                  variant="outlined"
                  value={clientIdentifier}
                  onChange={(event) => setClientIdentifier(event.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="1234567890"
                  disabled={isLoading}
                  sx={modalStyles.inputField}
                />
              </Stack>

              {headOfficeOnParentOrganizationPageEnabled && (
                <Stack direction="column" spacing={1} paddingBottom="10px">
                  <Typography sx={modalStyles.label}>Head Office Id</Typography>
                  <TextField
                    variant="outlined"
                    value={headOfficeId}
                    onChange={(event) => setHeadOfficeId(event.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="0001234"
                    disabled={isLoading}
                    sx={modalStyles.inputField}
                  />
                </Stack>
              )}

              {/* Year End Date Picker */}
              <Stack direction="column" spacing={1}>
                <Typography sx={modalStyles.label}>Year end date</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateField
                    format="MM/DD"
                    value={yearEndDate}
                    onChange={(newValue) => setYearEndDate(newValue)}
                    disabled={isLoading}
                    sx={modalStyles.datePicker}
                    placeholder="12/31"
                  />
                </LocalizationProvider>
              </Stack>

              {/* Admin Users Section */}
              {showAdminSection && (
                <Stack direction="column" spacing="12px">
                  {adminUsers.map((user, index) => (
                    <Stack
                      key={user.id}
                      direction="row"
                      alignItems="flex-start"
                    >
                      {/* Admin User Search */}
                      <Stack direction="column" flex={1}>
                        {index === 0 && (
                          <Typography sx={modalStyles.label}>
                            Assign Admin
                          </Typography>
                        )}
                        <Autocomplete
                          options={user.searchResults}
                          getOptionLabel={(option) => option?.email || ''}
                          isOptionEqualToValue={(option, value) =>
                            option?.id === value?.id
                          }
                          value={user.selectedUser}
                          onChange={(event, newValue) => {
                            updateAdminUser(index, {
                              selectedUser: newValue,
                              searchInput: newValue?.email || '',
                            });
                          }}
                          inputValue={user.searchInput}
                          onInputChange={(event, newInputValue, reason) => {
                            if (reason === 'input') {
                              updateAdminUser(index, {
                                searchInput: newInputValue,
                              });
                              debouncedSearch(newInputValue, index);
                            } else if (
                              reason === 'reset' &&
                              user.selectedUser
                            ) {
                              updateAdminUser(index, {
                                searchInput: user.selectedUser.email,
                              });
                            }
                          }}
                          loading={user.searchLoading}
                          disabled={isLoading}
                          autoComplete
                          includeInputInList
                          filterOptions={(options, params) => {
                            if (!params.inputValue.trim()) return [];
                            return options.filter((option) =>
                              option.email
                                .toLowerCase()
                                .includes(params.inputValue.toLowerCase())
                            );
                          }}
                          noOptionsText={
                            user.searchLoading
                              ? 'Searching...'
                              : user.searchInput.trim()
                                ? 'No users found'
                                : 'Start typing to search users'
                          }
                          sx={modalStyles.adminInputField}
                          componentsProps={{
                            paper: {
                              sx: {
                                borderRadius: '8px',
                                border: '1px solid #E1E5E4',
                                boxShadow: 'none',
                                '& .MuiAutocomplete-listbox': {
                                  '& .MuiAutocomplete-option': {
                                    color: '#162523',
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 500,
                                    lineHeight: '130%',
                                    letterSpacing: '-0.14px',
                                  },
                                },
                                '& .MuiAutocomplete-noOptions': {
                                  color: '#162523',
                                  fontSize: '14px',
                                  fontStyle: 'normal',
                                  fontWeight: 500,
                                  lineHeight: '130%',
                                  letterSpacing: '-0.14px',
                                },
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search user by email"
                              variant="outlined"
                              inputProps={{
                                ...params.inputProps,
                                autoComplete: 'new-password',
                                'data-1p-ignore': true,
                                'data-lpignore': true,
                                'data-form-type': 'other',
                                name: `admin-search-${index}`,
                                type: 'search',
                              }}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {user.searchLoading ? (
                                      <CircularProgress
                                        color="inherit"
                                        size={20}
                                      />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: '14px',
                                  color: '#162523',
                                }}
                              >
                                {option.email}
                              </Typography>
                            </Box>
                          )}
                        />
                      </Stack>

                      {/* Remove Button */}
                      <Stack
                        direction="column"
                        alignItems="center"
                        justifyContent="flex-start"
                        sx={{paddingTop: index === 0 ? '32px' : '0px'}}
                      >
                        <IconButton
                          onClick={() => removeAdminUser(index)}
                          disabled={isLoading}
                          sx={{
                            padding: '9px',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <CircleMinus size={20} color="#000000" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  ))}

                  {/* Add More Admin Users Button */}
                  <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
                    <Button
                      variant="text"
                      startIcon={
                        <img
                          src={PlusAddIcon}
                          alt="Add"
                          width={16}
                          height={16}
                        />
                      }
                      onClick={addAdminUser}
                      disabled={isLoading}
                      sx={{
                        color: '#26A993',
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        padding: '8px 12px',
                        justifyContent: 'flex-start',
                        width: 'auto',
                        minWidth: 'auto',
                        '&:hover': {
                          backgroundColor: 'rgba(38, 169, 147, 0.04)',
                        },
                      }}
                    >
                      Add more admins
                    </Button>
                  </Box>
                </Stack>
              )}

              {/* Add Admin Button - only show if admin section is not visible */}
              {!showAdminSection && (
                <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
                  <Button
                    variant="text"
                    startIcon={
                      <img src={PlusAddIcon} alt="Add" width={16} height={16} />
                    }
                    onClick={handleAddAdmin}
                    disabled={isLoading}
                    sx={{
                      color: '#26A993',
                      fontSize: '14px',
                      fontWeight: 500,
                      textTransform: 'none',
                      padding: '8px 12px',
                      justifyContent: 'flex-start',
                      width: 'auto',
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'rgba(38, 169, 147, 0.04)',
                      },
                    }}
                  >
                    Add admin
                  </Button>
                </Box>
              )}
            </Stack>
            <Divider orientation="horizontal" sx={modalStyles.divider} />
            <Stack
              direction="row"
              paddingY="1rem"
              paddingLeft="1.5rem"
              paddingRight="1rem"
              flex={1}
              justifyContent="flex-end"
              alignItems="center"
              spacing="0.5rem"
            >
              <Button
                variant="outlined"
                sx={modalStyles.cancelButton}
                onClick={handleClose}
                disabled={isLoading}
              >
                <Typography style={modalStyles.cancelButtonLabel}>
                  Cancel
                </Typography>
              </Button>
              <LoadingButton
                disabled={!isButtonEnabled || isLoading}
                variant="contained"
                sx={{
                  ...theme.button.primary.large,
                  '&.Mui-disabled': {
                    opacity: 0.6,
                    cursor: 'not-allowed',
                    color: '#fff',
                    backgroundColor: '#34A290',
                  },
                }}
                onClick={handleCreate}
                loading={isLoading}
                loadingIndicator={
                  <Loading
                    loading={isLoading}
                    loadingProps={{color: BLACK_100}}
                  />
                }
              >
                {isEditMode ? 'Update' : 'Create'}
              </LoadingButton>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

AddCompanyModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onShowSnackbar: PropTypes.func.isRequired,
  editingClient: PropTypes.object,
};

export default AddCompanyModal;
